import { EventDropArg } from '@fullcalendar/core'
import { CalendarType, TenantType } from '@xeetr/types/enums'
import {
  FoodBookingModel,
  ServiceBookingModel,
  ViewEmployee,
  ViewRoom,
  ViewTable,
} from '@xeetr/types/models'
import { endOfMonth, endOfWeek, startOfMonth, startOfWeek } from 'date-fns'
import { fromZonedTime, toZonedTime } from 'date-fns-tz'

export const getStartAndEndDatesForCalendar = (
  selectedDate: Date,
  calendarType: CalendarType,
  timezone?: string
) => {
  let fromDate: Date
  let untilDate: Date

  if (timezone) {
    const zonedDate = toZonedTime(selectedDate, timezone)

    switch (calendarType) {
      case CalendarType.DAY:
        fromDate = fromZonedTime(zonedDate, timezone)
        untilDate = fromZonedTime(zonedDate, timezone)
        break
      case CalendarType.WEEK:
        const weekStart = startOfWeek(zonedDate)
        const weekEnd = endOfWeek(zonedDate)
        fromDate = fromZonedTime(weekStart, timezone)
        untilDate = fromZonedTime(weekEnd, timezone)
        break
      case CalendarType.MONTH:
        const monthStart = startOfMonth(zonedDate)
        const monthEnd = endOfMonth(zonedDate)
        fromDate = fromZonedTime(monthStart, timezone)
        untilDate = fromZonedTime(monthEnd, timezone)
        break
      default:
        throw new Error('Calendertype unknown')
    }
  } else {
    // Fallback to local timezone functions
    switch (calendarType) {
      case CalendarType.DAY:
        fromDate = selectedDate
        untilDate = selectedDate
        break
      case CalendarType.WEEK:
        fromDate = startOfWeek(selectedDate)
        untilDate = endOfWeek(selectedDate)
        break
      case CalendarType.MONTH:
        fromDate = startOfMonth(selectedDate)
        untilDate = endOfMonth(selectedDate)
        break
      default:
        throw new Error('Calendertype unknown')
    }
  }

  return { fromDate, untilDate }
}

export const prepareBooking = (
  booking: FoodBookingModel | ServiceBookingModel,
  tenantType: TenantType,
  argument: EventDropArg
): FoodBookingModel | ServiceBookingModel => {
  switch (tenantType) {
    case TenantType.FOOD:
      return resourceToBookingFood(booking as FoodBookingModel, argument)
    case TenantType.SERVICE:
      return resourceToBookingService(booking as ServiceBookingModel, argument)
  }
}

const resourceToBookingFood = (booking: FoodBookingModel, argument: EventDropArg) => {
  const newResourceId = argument.newResource?._resource.id
  const oldResourceId = argument.oldResource?._resource.id

  const oldRoomId = booking.room?.id
  const newRoomId =
    argument.newResource?._resource.parentId ||
    (argument.newResource?._resource.id === 'no_room_assignment'
      ? undefined
      : argument.newResource?._resource.id)

  if (!Array.isArray(booking.tables)) booking.tables = []

  // if the booking moves to a different room we must remove any tables
  // that were previously assigned from the old room; otherwise dragging
  // from one table to another room would leave stray table entries.
  if (oldRoomId && newRoomId && oldRoomId !== newRoomId) {
    booking.tables = []
  } else {
    // otherwise just drop the old resource id (single-table move)
    booking.tables = booking.tables.filter((t) => t.id !== oldResourceId)
  }

  if (argument.newResource?._resource.parentId) {
    booking.tables.push({ id: newResourceId } as ViewTable)
    booking.room = { id: argument.newResource?._resource.parentId } as ViewRoom
  } else {
    booking.room =
      newResourceId === 'no_room_assignment'
        ? undefined
        : ({
            id: newResourceId,
          } as ViewRoom)
  }

  return booking
}
const resourceToBookingService = (booking: ServiceBookingModel, argument: EventDropArg) => {
  const newResourceId = argument.newResource?._resource.id
  if (!newResourceId) return booking
  booking.employee = { id: newResourceId } as ViewEmployee
  return booking
}

export const formatSlotDuration = (minutes: number): string => {
  const hrs = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}`
}
